/**
 * [INPUT]: 依赖 CommonChartContext、framer-motion 与 palette seed，接受通用或自定义 content formatter
 * [OUTPUT]: 对外提供跟随 scrub 位置滑行、保留退场内容的 Tooltip
 * [POS]: 全图表家族共享的 DOM 信息层，不介入 Canvas 绘制循环
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */
"use client";
import { AnimatePresence, m as Motion } from "framer-motion"
import { useRef } from "react"
import { useCommonChart } from "./common-context"
import { cn } from "./lib"
import { rgb } from "./palette"

const VARIANT = {
  default: "bg-popover",
  "frosted-glass": "bg-popover/70 backdrop-blur-sm",
}

/**
 * Floating hover tooltip. Reads the shared common context so it works in every
 * chart family. It glides between points and fades in/out (instead of snapping),
 * and dims unselected series/slices.
 */
export function Tooltip({
  labelKey,
  labelFormatter,
  valueFormatter,
  content,
  variant = "default"
}) {
  const chart = useCommonChart()
  const show = chart.ready && chart.hoverIndex != null

  // Retain the last hovered index so the card keeps its content while fading
  // out — adjust-state-during-render (no refs in render).
  const lastIndex = useRef(0)
  if (chart.hoverIndex != null) lastIndex.current = chart.hoverIndex
  const index = chart.hoverIndex ?? lastIndex.current

  const heading = chart.heading(index, labelKey)
  const items = chart.itemsAt(index)
  const row = chart.rowAt(index)
  const renderedContent = content?.({ index, heading, items, row })

  return (
    <AnimatePresence>
      {show && items.length > 0 && (
        <Motion.div
          key="dither-tooltip"
          initial={{
            opacity: 0,
            x: "-50%",
            y: "-115%",
            top: chart.tooltipTop,
            left: chart.tooltipLeft,
          }}
          animate={{
            opacity: 1,
            x: "-50%",
            y: "-115%",
            top: chart.tooltipTop,
            left: chart.tooltipLeft,
          }}
          exit={{ opacity: 0 }}
          transition={{
            type: "spring",
            stiffness: 520,
            damping: 38,
            mass: 0.6,
          }}
          className={cn(
            "pointer-events-none absolute z-10 rounded-md border px-2 py-1 shadow-sm",
            VARIANT[variant]
          )}>
          {renderedContent ?? <>
          {heading && (
            <div className="mb-0.5 font-mono text-[10px] text-muted-foreground">
              {labelFormatter ? labelFormatter(heading, row) : heading}
            </div>
          )}
          <div className="flex flex-col gap-0.5">
            {items.map((item) => (
              <div
                key={item.name}
                className="flex items-center gap-1.5 font-mono text-[11px] text-popover-foreground tabular-nums"
                style={{ opacity: item.dimmed ? 0.4 : 1 }}>
                <span
                  className="size-2 rounded-[1px]"
                  style={{ backgroundColor: rgb(item.seed.fill) }} />
                <span className="text-muted-foreground">{item.label}</span>
                <span className="ml-auto pl-2 text-foreground">
                  {valueFormatter
                    ? valueFormatter(item.value, item.name)
                    : item.value.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
          </>}
        </Motion.div>
      )}
    </AnimatePresence>
  );
}

Tooltip.chartLayer = "dom"
